import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { UserCompany } from '@/store/api/userApi';

import { getCompanyRoleColor } from '@/utils/helpers';

import {
  chipSx,
  companyNameSx,
  labelSx,
  menuItemContainerSx,
  menuItemTextSx,
  menuPaperSx,
} from './UserCompanyMenu.styles';

interface UserCompanyMenuProps {
  selectedCompanyId: string | null;
  onCompanyChange: (companyId: string) => void;
  userCompanies: UserCompany[];
}

export default function UserCompanyMenu({
  selectedCompanyId,
  onCompanyChange,
  userCompanies,
}: Readonly<UserCompanyMenuProps>) {
  const selectedCompany =
    userCompanies?.find(c => c.id === selectedCompanyId) ?? null;

  if (userCompanies.length > 1) {
    return (
      <Autocomplete
        fullWidth
        size="small"
        options={userCompanies}
        value={selectedCompany}
        onChange={(_, company) => company && onCompanyChange(company.id)}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        slotProps={{
          listbox: { sx: menuPaperSx },
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <MenuItem key={key} {...rest}>
              <Box sx={menuItemContainerSx}>
                <Chip
                  label={option.roleName}
                  size="small"
                  color={getCompanyRoleColor(option.companyRole)}
                  variant="outlined"
                  sx={chipSx}
                />
                <Box sx={menuItemTextSx}>{option.name}</Box>
              </Box>
            </MenuItem>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Current Company"
            onKeyDown={e => e.stopPropagation()}
          />
        )}
      />
    );
  } else {
    // Show company name for single company users
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={labelSx}>
          Company
        </Typography>
        <Typography variant="body1" sx={companyNameSx}>
          {userCompanies[0]?.name || 'No Company'}
        </Typography>
      </Box>
    );
  }
}
